<template>
  <div class="d-widget-activity-stream-card d-widget-list-card">
    <div class="d-widget-list-card__header">
      <div>
        <div class="d-widget-list-card-title">
          {{widget.name}}
        </div>
      </div>

      <!-- SS03547: hidden when the workspace restricts "everyone" to admins and this user is not one -->
      <div class="activity-stream-card-mode" v-if="activityEveryoneAllowed">
        <font-awesome-icon :icon="['fas', 'user']" class="activity-stream-card-mode-icon" />
        <div
          class="activity-stream-card-mode-option"
          :class="{'activity-stream-card-mode-option--active': !isEveryone}"
          @click="toggleEveryone"
        >
          me
        </div>
        <div
          class="activity-stream-card-mode-option"
          :class="{'activity-stream-card-mode-option--active': isEveryone}"
          @click="toggleEveryone"
        >
          everyone
        </div>
      </div>
    </div>

    <div class="d-widget-list-card__content fts-scrollbar fts-scrollbar-overlay">
      <template v-if="!initial && loading">
        <skeleton-activity-stream class="activity-stream" />
        <skeleton-activity-stream class="activity-stream" />
        <skeleton-activity-stream class="activity-stream" />
      </template>

      <template v-else-if="isResult">
        <template v-for="item in widget.group">
          <div class="d-widget-locus-card-wrapper" v-if="widgetData[item.id] && widgetData[item.id].length" :key="item.id">
            <div class="d-widget-list-card-wrapper-description" v-if="item.label">
              <template v-if="item.label === true">&nbsp;</template>
              <template v-else>{{item.label}}</template>
            </div>
            <div
              v-for="(x, ind) in widgetData[item.id]"
              :key="`activity-stream-${ind}-${x.id}`"
              class="activity-stream"
              @click="entryView(x)"
            >
              <fts-profile
                :profile-photo-size="50"
                :user-name="x.audit && x.audit.updatedBy"
                profile-name-class="activity-stream-info__r"
                class="activity-stream-info"
              >
                <template #profileName="{userProfileName}">
                  <div class="d-widget-locus-card-header activity-stream-info-header">
                    <div class="activity-stream-info-header-title">
                      <span class="profile-name">{{userProfileName}}</span>
                      <template v-if="x.activityName">
                        has done <span class="activity-name">{{x.activityName}}</span> on 
                      </template>
                      <span v-else-if="x.historyType === 1">created</span>
                      <span v-else-if="x.historyType === 2">updated</span>
                      <span v-else-if="x.historyType === 12">managed</span>
                      <span v-else-if="x.historyType === 4">change state on</span>  
                      <span v-else-if="x.historyType === 7">has done assigned on</span>
                      <span v-else-if="x.historyType === 8">imported</span>
                      <span v-else-if="x.historyType === 9">merged</span>
                      <span v-else>commented on</span>
                      <span class="title-name">{{x.title}}</span>
                    </div>
                    <!-- state -->
                    <fts-state
                      :state="x.toState"
                      :states="states"
                      class="d-widget-locus-card-state"
                      v-if="x.toState"
                    />
                  </div>

                  <div class="d-widget-locus-card-content">
                    <div class="d-widget-locus-card-content__l">
                      <!-- document -->
                      <div class="d-widget-locus-card-document-id">
                        {{x.document.documentId}}
                      </div>
                      <!-- module -->
                      <fts-label-group
                        :item-id="x.vectorId"
                        :list="modules"
                        emoji
                        :emoji-size="12"
                        class="d-widget-locus-card-module"
                        v-if="x.vectorId"
                      />
                    </div>
                    <!-- audit created -->
                    <div class="d-widget-locus-card-audit">
                      <div class="d-widget-locus-card-audit-by">by</div>
                      <fts-profile
                        :userName="x.audit.createdBy"
                        class="d-widget-locus-card-audit-profile"
                      />
                      <fts-tippy
                        class="d-widget-locus-card-audit-date"
                        :content="moment(x.audit.createdDate).format('llll')"
                      >
                        <template #trigger>
                          {{dateFormat(x.audit.createdDate)}}
                        </template>
                      </fts-tippy>
                    </div>
                  </div>

                  <div class="d-widget-locus-card-footer activity-stream-info-footer">
                    <!-- collaboration -->
                    <div 
                      class="d-widget-locus-card-collaboration fts-collaboration" 
                      v-if="x.collaborationFlags && [1, 2, 5, 10].indexOf(x.collaborationFlags) > -1"
                    >
                      <div class="collaboration-wrapper" v-if="x.collaborationFlags === 2 || x.collaborationFlags === 10">
                        <div 
                          class="collaboration-trigger collaboration-intention"
                          :class="{ 'collaboration-last': x.collaborationFlags === 10}">
                          <i class="fts-icon-exclamation-triangle"></i>
                        </div>
                      </div>
                      <div class="collaboration-wrapper" v-if="x.collaborationFlags === 1 || x.collaborationFlags === 5">
                        <div 
                          class="collaboration-trigger collaboration-comment"
                          :class="{ 'collaboration-last': x.collaborationFlags === 5}">
                          <i class="fts-icon-comment"></i>
                        </div>
                        <div class="collaboration-count" v-if="x.commentCount">{{x.commentCount}}</div>
                      </div>
                    </div>
                    <!-- audit created -->
                    <template v-if="x.audit && x.audit.createdDate && x.historyType === 1">
                      <fts-tippy class="d-widget-locus-card-audit-date" :content="moment(x.audit.createdDate).format('llll')">
                        <template #trigger>{{dateFormat(x.audit.createdDate)}}</template>
                      </fts-tippy>
                    </template>
                    <!-- audit updated -->
                    <template v-else-if="x.audit && x.audit.updatedBy">
                      <fts-tippy class="d-widget-locus-card-audit-date" :content="moment(x.audit.updatedDate).format('llll')">
                        <template #trigger>{{dateFormat(x.audit.updatedDate)}}</template>
                      </fts-tippy>
                    </template>
                  </div>
                </template>
              </fts-profile>
              <!-- content -->
              <div
                class="activity-stream-content"
                v-html="x.content"
                v-if="commentContent(x.content)"
              />
              <!-- attachments -->
              <fts-file 
                :prefixURL="`${x.vectorId}-comment/`" 
                :data="x.attachments" 
                downloadable 
                class="activity-stream-attachments stopPropagation" />
            </div>
          </div>
        </template>
      </template>

      <div class="d-widget-list-card-no-result" v-else>
        No result.
      </div>
    </div>

    <div class="d-widget-overlay" v-if="initial && loading">
      <fts-spinner show :size="24" />
    </div>
  </div>
</template>

<script>
import moment from "moment";

import commons from '../../commons/commons';
import { htmlValidator } from "../../commons/validator";

import SkeletonActivityStream from "../../elements/skeleton-screen/dashboard/SkeletonActivityStream";
import FTSFile from "../../elements/file/FTSFile";
import FTSLabelGroup from "../../elements/FTSLabelGroup";
import FTSProfile from "../../elements/FTSProfile";
import FTSSpinner from '../../elements/FTSSpinner';
import FTSState from "../../elements/FTSState";
import FTSTippy from "../../elements/FTSTippy";

export default {
  props: {
    modules: Array,
    states: Array,
    widget: Object,
    widgetData: Object,
    loading: Boolean,
    initial: Boolean,
    activityEveryoneAllowed: {
      type: Boolean,
      default: true
    }
  },
  watch: {
    /* SS03547: the workspace restricted "everyone" after the switch was flipped - snap the card
       back to "me". No emit: the dashboard has already reset its own flag from the server reply
       and reloaded, so emitting here would only trigger a second, identical load. */
    activityEveryoneAllowed(allowed) {
      if (!allowed) this.isEveryone = false;
    }
  },
  components: {
    "skeleton-activity-stream": SkeletonActivityStream,
    "fts-file": FTSFile,
    "fts-label-group": FTSLabelGroup,
    "fts-profile": FTSProfile,
    "fts-spinner": FTSSpinner,
    "fts-state": FTSState,
    "fts-tippy": FTSTippy
  },
  data: () => ({
    isEveryone: false,
  }),
  methods: {
    moment,
    dateFormat(date) {
      return commons.momentRelativeTime(date);
    },
    commentContent(content) {
      return htmlValidator(content)
    },
    entryView(item) {
      if (commons.elementStopPropagation()) return;
      this.$emit("entry:view", { id: item.id, vectorId: item.vectorId});
    },
    toggleEveryone() {
      this.isEveryone = !this.isEveryone;
      window.eventBus.$emit("toggleActivityStreamMode", this.isEveryone);
    }
  },
  computed: {
    isResult() {
      return Array.isArray(this.widget && this.widget.group) ?
        this.widget.group.some((x) => this.widgetData[x.id] && this.widgetData[x.id].length) :
        false
      ;
    }
  }
}
</script>

<style lang="scss" scoped>
@use "./src/components/dashboard/widgets/scss/style";
</style>

<style lang="scss">
@use "./src/assets/scss/vars" as *;
@use "./src/assets/scss/colors" as *;

.d-widget-activity-stream-card {
  color: var(--fts-text);
  .activity-stream-card-mode {
    display: flex;
    align-items: center;
    margin-left: 0.75rem;
    font-size: 0.875rem;
    color: var(--fts-default);
    .activity-stream-card-mode-icon {
      margin-right: 0.25rem;
      font-size: 0.75rem;
    }
    .activity-stream-card-mode-option {
      @include user-select-none;
      position: relative;
      padding: 0 0.375rem;
      cursor: pointer;
      &.activity-stream-card-mode-option--active {
        color: var(--fts-primary);
      }
      &:not(:last-child) {
        &::after {
          content: "";
          position: absolute;
          right: 0;
          top: 0.275rem;
          bottom: 0.275rem;
          border-right: 1px solid var(--fts-border);
        }
      }
    }
  }
  .activity-stream {
    margin-bottom: 0.75rem;
    margin-left: 0.75rem;
    margin-right: 0.75rem;
    padding: 0.75rem;
    border-radius: 0.5rem;
    background: var(--fts-card-background);
    color: var(--fts-text);
    box-shadow: var(--fts-elevation-2);
    cursor: pointer;
    &:first-child {
      margin-top: 0.25rem;
    }
  }
  .activity-stream-info {
    align-items: flex-start;
    .activity-stream-info__r {
      flex: 1;
      min-width: 0;
    }
  }
  .activity-stream-info-header {
    .activity-stream-info-header-title {
      flex: 1 1 70%;
      font-size: 0.875rem;
      line-height: 1.25rem;
      word-break: break-word;
    }
    .profile-name {
      font-weight: 600;
    }
    .activity-name {
      font-weight: 500;
    }
    .title-name {
      font-weight: 500;
    }
  }
  .activity-stream-info-footer {
  }
  .activity-stream-content {
    display: inline-block;
    margin-top: 0.375rem;
    padding: 1rem 0.75rem;
    word-break: break-all;
    white-space: pre-wrap;
    font-size: 1.125rem;
  }
  .activity-stream-attachments {
    margin-top: 0.375rem;
  }
}
</style>